// ── useCauldronToken ─────────────────────────────────────────────
//
// Resolves a CashToken from the Cauldron indexer by ticker symbol OR 64-char
// token id. Returns a loading flag plus the normalised token (or null when no
// such token has an active pool). Read-only — see src/lib/cauldron.ts.

import { useState, useEffect } from 'react';
import {
  resolveTokenBySymbol,
  resolveTokenById,
  fetchPoolsForToken,
  fetchTokenList,
  type CauldronToken,
  type CauldronPool,
  type TokenSortField,
} from '../lib/cauldron';

interface TokenState {
  token: CauldronToken | null;
  loading: boolean;
}

/** Resolve by ticker symbol (e.g. "LANDO"). */
export function useCauldronTokenBySymbol(symbol: string | undefined): TokenState {
  const [state, setState] = useState<TokenState>({ token: null, loading: !!symbol });

  useEffect(() => {
    if (!symbol) { setState({ token: null, loading: false }); return; }
    let cancelled = false;
    setState(s => ({ token: s.token, loading: true }));
    resolveTokenBySymbol(symbol)
      .then(token => { if (!cancelled) setState({ token, loading: false }); })
      .catch(() => { if (!cancelled) setState({ token: null, loading: false }); });
    return () => { cancelled = true; };
  }, [symbol]);

  return state;
}

/** Resolve by 64-char hex token id (for the /token/:id page). */
export function useCauldronTokenById(tokenId: string | undefined): TokenState {
  const [state, setState] = useState<TokenState>({ token: null, loading: !!tokenId });

  useEffect(() => {
    if (!tokenId) { setState({ token: null, loading: false }); return; }
    let cancelled = false;
    setState(s => ({ token: s.token, loading: true }));
    resolveTokenById(tokenId)
      .then(token => { if (!cancelled) setState({ token, loading: false }); })
      .catch(() => { if (!cancelled) setState({ token: null, loading: false }); });
    return () => { cancelled = true; };
  }, [tokenId]);

  return state;
}

/** Token discovery list, sorted by the given field. */
export function useCauldronTokenList(by: TokenSortField): { tokens: CauldronToken[]; loading: boolean } {
  const [tokens, setTokens] = useState<CauldronToken[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTokenList(by, 100)
      .then(t => { if (!cancelled) { setTokens(t); setLoading(false); } })
      .catch(() => { if (!cancelled) { setTokens([]); setLoading(false); } });
    return () => { cancelled = true; };
  }, [by]);

  return { tokens, loading };
}

/** Active liquidity pools for a token id. */
export function useCauldronPools(tokenId: string | undefined): { pools: CauldronPool[]; loading: boolean } {
  const [pools, setPools] = useState<CauldronPool[]>([]);
  const [loading, setLoading] = useState(!!tokenId);

  useEffect(() => {
    if (!tokenId) { setPools([]); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    fetchPoolsForToken(tokenId)
      .then(p => { if (!cancelled) { setPools(p); setLoading(false); } })
      .catch(() => { if (!cancelled) { setPools([]); setLoading(false); } });
    return () => { cancelled = true; };
  }, [tokenId]);

  return { pools, loading };
}
